import { useState, type KeyboardEvent } from 'react';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  balance: string;
  mode?: 'default' | 'user';
  disabled?: boolean;
}

export default function ChatInput({ onSendMessage, balance, mode = 'default', disabled = false }: ChatInputProps) {
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-border-soft px-2.5 sm:px-3 py-2 bg-gradient-to-b from-[#070814]/96 to-bg-main/98 flex flex-col gap-1.5 flex-shrink-0">
      <div className="flex gap-2 items-end">
        <div className={`flex-1 bg-bg-input rounded-xl border ${mode === 'user' && !disabled ? 'border-accent/50 shadow-[0_0_12px_rgba(226,183,20,0.12)] transition-all duration-700' : 'border-border-soft'} px-2.5 py-1.5 flex flex-col gap-0.5 min-w-0`}>
          {mode === 'user' && !disabled && !message && (
            <div className="text-[0.68rem] text-accent font-medium px-1 pt-0.5 animate-pulse">
              ✨ O consultor terminou. É a sua vez de falar.
            </div>
          )}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={mode === 'user' ? (disabled ? 'Aguarde a resposta do consultor…' : 'Escreva a sua mensagem…') : 'Escreva a sua resposta para o cliente…'}
            className={`border-none bg-transparent text-text text-[0.82rem] resize-none max-h-[90px] min-h-[32px] outline-none placeholder:text-[#6f7693] w-full ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            rows={1}
          />
          <div className="flex justify-between items-center text-[0.65rem] sm:text-[0.7rem] text-text-soft gap-2">
            {mode !== 'user' ? (
              <span className="hidden sm:inline">
                Sugestões rápidas:{' '}
                <span className="text-accent cursor-pointer hover:underline">Relaxar cliente</span> ·{' '}
                <span className="text-accent cursor-pointer hover:underline">Explicar próxima etapa</span>
              </span>
            ) : (
              <span />
            )}
            <span className="whitespace-nowrap ml-auto">
              Saldo: <strong>{balance} min</strong>
            </span>
          </div>
        </div>
        {mode !== 'user' && (
          <button 
            className="w-8 h-8 rounded-full border border-border-soft bg-black/20 text-text flex items-center justify-center text-[0.85rem] cursor-pointer hover:bg-white/5 transition-colors flex-shrink-0"
            title="Respostas guardadas"
            disabled={disabled}
          >
            ✎
          </button>
        )}
        <button 
          onClick={handleSend}
          disabled={disabled}
          className={`w-8 h-8 rounded-full border border-border-soft text-text flex items-center justify-center text-[0.85rem] transition-colors flex-shrink-0 ${disabled ? 'bg-black/10 opacity-50 cursor-not-allowed' : 'bg-black/20 cursor-pointer hover:bg-white/5'}`}
          title="Enviar mensagem"
        >
          ➤
        </button>
      </div>
      {mode !== 'user' ? (
        <div className="text-[0.65rem] sm:text-[0.7rem] text-text-soft flex justify-between gap-2">
          <span className="hidden sm:inline">Vista de consultor: o cliente não vê avisos internos, saldo em min é só para consultor.</span>
          <span className="ml-auto">Enter = enviar · Shift+Enter = nova linha</span>
        </div>
      ) : (
        <div className="text-[0.65rem] sm:text-[0.7rem] text-text-soft flex justify-end">
          <span>Enter = enviar · Shift+Enter = nova linha</span>
        </div>
      )}
    </div>
  );
}
